import { Injectable, inject, signal, computed, OnDestroy } from '@angular/core';
import { Subject, firstValueFrom, takeUntil } from 'rxjs';
import { ApiError } from '../../abstract/api-error';
import { BaseService } from '../../abstract/base.service';
import { WebSocketService } from '../services/websocket.service';
import { MembershipTierService } from '../api-services/membership-tier.service';
import { isProRequiredError } from '../utils/http-error-utils';
import { AgremoAnalysis } from '../data-models/agremo-analysis.model';
import { AgremoAnalysisStatus } from '../data-models/agremo-analysis-status.model';
import { AgremoProgressEvent } from '../response-models/agremo-progress-event.model';
import { AgremoCompleteEvent } from '../response-models/agremo-complete-event.model';
import { AgremoFailedEvent } from '../response-models/agremo-failed-event.model';
import { CreateAnalysisDto } from '../request-models/create-analysis-dto.model';
import { toAgremoAnalysisFiles, toCreateAnalysisRequest } from '../utils/agremo-utils';
import { withChanges } from '../utils/model-utils';

/** Query options for the analyses list. */
export interface AgremoAnalysisListOptions {
  page?: number;
  limit?: number;
  status?: AgremoAnalysisStatus;
  mapId?: string;
}

/**
 * Agremo analyses: `/user/agremo/analyses[/:id]` (hub `user-agremo-controller.ts`), paged with `page` / `limit`.
 * Listing and creating require Pro (`pro_required`). Analysis types and the integration status live in
 * `AgremoService`; maps in `AgremoMapService`.
 *
 * Holds the last loaded page as signal state and applies real-time progress from the WebSocket.
 *
 * Reference: /agent-os/specs/2026-01-22-agremo-api-integration/spec.md
 */
@Injectable({
  providedIn: 'root'
})
export class AgremoAnalysisService extends BaseService<AgremoAnalysis> implements OnDestroy {
  private webSocketService = inject(WebSocketService);
  private membershipTierService = inject(MembershipTierService);
  private destroy$ = new Subject<void>();

  // Signal-based state
  private analysesSignal = signal<AgremoAnalysis[]>([]);
  private loadingSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);
  // True when the ORG'S tier dropped to Core between the page loading and this call landing (e.g. a
  // subscription cancelled directly in Stripe). Distinct from errorSignal so the component can redirect
  // to the preview page instead of rendering a raw failure banner. See loadAnalyses.
  private proRequiredSignal = signal<boolean>(false);
  private totalCountSignal = signal<number>(0);
  private currentPageSignal = signal<number>(1);
  private pageSizeSignal = signal<number>(20);

  // Track WebSocket subscription state
  private isSubscribed = false;

  // Public computed signals
  readonly analyses = computed(() => this.analysesSignal());
  readonly isLoading = computed(() => this.loadingSignal());
  readonly error = computed(() => this.errorSignal());
  readonly proRequired = computed(() => this.proRequiredSignal());
  readonly totalCount = computed(() => this.totalCountSignal());
  readonly currentPage = computed(() => this.currentPageSignal());
  readonly pageSize = computed(() => this.pageSizeSignal());

  // Computed stats
  readonly processingAnalyses = computed(() =>
    this.analysesSignal().filter(a => a.status === 'processing' || a.status === 'queued' || a.status === 'downloading')
  );
  readonly completedAnalyses = computed(() => this.analysesSignal().filter(a => a.status === 'completed'));
  readonly failedAnalyses = computed(() => this.analysesSignal().filter(a => a.status === 'failed'));

  constructor() {
    super('user/agremo/analyses', AgremoAnalysis);
    this.webSocketService.connectionState$.pipe(takeUntil(this.destroy$)).subscribe(state => {
      if (state === 'authenticated' && !this.isSubscribed) {
        this.setupWebSocketSubscriptions();
      } else if (state === 'disconnected') {
        this.isSubscribed = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ============================================
  // API METHODS
  // ============================================

  /**
   * Load a page of analyses into signal state, optionally for one map. A `pro_required` failure sets
   * `proRequired` (and refreshes the cached tier) instead of `error`.
   */
  async loadAnalyses(options?: AgremoAnalysisListOptions): Promise<void> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);
    // A reused instance could hold a stale flag from a prior Core-org load.
    this.proRequiredSignal.set(false);
    try {
      const page = await firstValueFrom(this.getPage({ query: { ...options } }));
      this.analysesSignal.set(page.items);
      this.totalCountSignal.set(page.paging.totalItems);
      this.currentPageSignal.set(page.paging.page);
      this.pageSizeSignal.set(page.paging.pageSize);
    } catch (error: unknown) {
      this.handleLoadError(error);
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /** One analysis with its files, field and boundary; replaces it in state. Resolves `null` on failure. */
  async getAnalysisById(id: string): Promise<AgremoAnalysis | null> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);
    try {
      const analysis = await firstValueFrom(this.get(id));
      if (analysis) {
        this.analysesSignal.update(analyses => analyses.map(a => (a.id === id ? analysis : a)));
      }
      return analysis;
    } catch (error: unknown) {
      this.recordError(error, 'Failed to fetch analysis');
      return null;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /** Request a new analysis of a ready map and prepend it to state. Resolves `null` on failure (see `error`). */
  async createAnalysis(dto: CreateAnalysisDto): Promise<AgremoAnalysis | null> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);
    try {
      const newAnalysis = await firstValueFrom(this.create(toCreateAnalysisRequest(dto)));
      if (newAnalysis) {
        this.analysesSignal.update(analyses => [newAnalysis, ...analyses]);
        this.totalCountSignal.update(count => count + 1);
      }
      return newAnalysis;
    } catch (error: unknown) {
      this.recordError(error, 'Failed to create analysis');
      return null;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  // ============================================
  // HELPER METHODS
  // ============================================

  /** Get analysis by ID from local state */
  getAnalysisFromState(id: string): AgremoAnalysis | undefined {
    return this.analysesSignal().find(a => a.id === id);
  }

  /** Get human-readable status text */
  getStatusText(status: AgremoAnalysisStatus): string {
    switch (status) {
      case 'pending': return 'Pending';
      case 'queued': return 'Queued';
      case 'processing': return 'Processing';
      case 'downloading': return 'Downloading Results';
      case 'completed': return 'Completed';
      case 'failed': return 'Failed';
      default: return status;
    }
  }

  /** Get Bootstrap badge class for status */
  getStatusBadgeClass(status: AgremoAnalysisStatus): string {
    switch (status) {
      case 'pending': return 'bg-secondary';
      case 'queued': return 'bg-info text-dark';
      case 'processing': return 'bg-warning text-dark';
      case 'downloading': return 'bg-info text-dark';
      case 'completed': return 'bg-success';
      case 'failed': return 'bg-danger';
      default: return 'bg-secondary';
    }
  }

  /** Get Bootstrap icon class for status */
  getStatusIcon(status: AgremoAnalysisStatus): string {
    switch (status) {
      case 'pending': return 'bi-clock';
      case 'queued': return 'bi-hourglass-split';
      case 'processing': return 'bi-arrow-repeat';
      case 'downloading': return 'bi-cloud-arrow-down';
      case 'completed': return 'bi-check-circle';
      case 'failed': return 'bi-exclamation-triangle';
      default: return 'bi-question-circle';
    }
  }

  /** Get file type label */
  getFileTypeLabel(fileType: string): string {
    switch (fileType) {
      case 'pdf': return 'PDF Report';
      case 'tiff': return 'GeoTIFF Imagery';
      case 'shp': return 'Shapefile';
      case 'png': return 'PNG Image';
      default: return fileType.toUpperCase();
    }
  }

  /** Get file type icon */
  getFileTypeIcon(fileType: string): string {
    switch (fileType) {
      case 'pdf': return 'bi-file-earmark-pdf';
      case 'tiff': return 'bi-file-earmark-image';
      case 'shp': return 'bi-file-earmark-zip';
      case 'png': return 'bi-file-earmark-image';
      default: return 'bi-file-earmark';
    }
  }

  /** Clear error state */
  clearError(): void {
    this.errorSignal.set(null);
  }

  // ============================================
  // WEBSOCKET SUBSCRIPTIONS
  // ============================================

  /** Listen for progress, complete and failed events once the socket is authenticated. */
  private setupWebSocketSubscriptions(): void {
    if (this.isSubscribed) return;
    this.isSubscribed = true;

    this.webSocketService.on<AgremoProgressEvent>('agremo-analysis:progress')
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => this.patchAnalysis(event.analysisId, { status: event.status, progress: event.progress }));

    this.webSocketService.on<AgremoCompleteEvent>('agremo-analysis:complete')
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => this.patchAnalysis(event.analysisId, {
        status: 'completed',
        progress: 100,
        ...(event.files ? { files: toAgremoAnalysisFiles(event.files) } : {}),
      }));

    this.webSocketService.on<AgremoFailedEvent>('agremo-analysis:failed')
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => this.patchAnalysis(event.analysisId, { status: 'failed', errorMessage: event.error, progress: 0 }));
  }

  /** Replaces an analysis in state with a copy (same class) carrying `changes`. */
  private patchAnalysis(analysisId: string, changes: Partial<AgremoAnalysis>): void {
    this.analysesSignal.update(analyses =>
      analyses.map(analysis => (analysis.id === analysisId ? withChanges(analysis, changes) : analysis))
    );
  }

  private handleLoadError(error: unknown): void {
    if (isProRequiredError(error)) {
      // Expected when the org's tier dropped to Core underneath an open session. The component redirects
      // to the preview page; refreshing the tier lets the sidebar and other guarded routes self-correct.
      this.proRequiredSignal.set(true);
      this.analysesSignal.set([]);
      this.totalCountSignal.set(0);
      void this.membershipTierService.fetchStatus();
      return;
    }
    this.recordError(error, 'Failed to load analyses');
  }

  private recordError(error: unknown, fallback: string): void {
    const apiError = ApiError.fromHttpError(error);
    console.error(`[AgremoAnalysisService] ${fallback}:`, apiError.message);
    this.errorSignal.set(apiError.errors[0] ?? fallback);
  }
}

